import React, { FC, useState, FormEvent } from 'react'
import styles from './GeneratePredictiveModelReportDialog.module.scss'
import { Dialog } from 'components/Dialog/Dialog'
import { Stack } from 'components/Stack'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/Button'
import { Alert } from 'components/Alert'
import { useFetchOrganization } from 'services/organization'
import { useGeneratePredictiveReport } from 'services/predictive-model'
import { Icon } from 'components/Icon'
import { useUserContext } from 'context/user-context'
import { ButtonLink } from 'components/ButtonLink'
import { UserRoles } from 'models/user'

type Steps = 'confirm' | 'download'

type GeneratePredictiveModelReportDialogProps = {
  onClose: () => void
  isOpen: boolean
  predictiveModelId: string
}

export const GeneratePredictiveModelReportDialog: FC<GeneratePredictiveModelReportDialogProps> = ({
  onClose,
  isOpen = false,
  predictiveModelId,
}) => {
  const { t } = useTranslation()
  const [step, setStep] = useState<Steps>('confirm')
  const { organization, status: orgStatus } = useFetchOrganization()
  const [generateReport, { data, status }] = useGeneratePredictiveReport(predictiveModelId)
  const availableReports = organization?.subscription?.predictiveReports || 0

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()

    generateReport(undefined, {
      onSuccess: () => {
        setStep('download')
      },
    })
  }

  if (!organization || orgStatus === 'loading') return null

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      aria-label={t('generate_predictive_report_dialog.title')}
      className={styles.generatePredictiveModelReportDialog}
    >
      <Stack>
        <div>
          <Heading>{t(`generate_predictive_report_dialog.${step}.title`)}</Heading>
          <Text>{t(`generate_predictive_report_dialog.${step}.desc`)}</Text>
        </div>

        {step === 'confirm' &&
          (availableReports > 0 ? (
            <ConfirmDownload
              status={status}
              availableReports={availableReports}
              onSubmit={onSubmit}
            />
          ) : (
            <NoReportsAvailable />
          ))}

        {step === 'download' && <DownloadReport data={data} />}
      </Stack>
    </Dialog>
  )
}

GeneratePredictiveModelReportDialog.displayName = 'GeneratePredictiveModelReportDialog'

const DownloadReport: FC<any> = ({ data }) => {
  if (!data) return null

  return (
    <a
      href={data.reportUrl}
      className={styles.downloadLink}
      target="_blank"
      rel="noopener noreferrer"
    >
      <Text weight="strong" className={styles.fileName} component="span">
        {data.name}
      </Text>

      <Icon name="download-line" />
    </a>
  )
}

const NoReportsAvailable: FC = () => {
  const { t } = useTranslation()
  const [currentUser] = useUserContext()
  const canPurchase = currentUser?.isRole(UserRoles.OWNER)

  return (
    <Stack space="medium">
      <Alert full tone="critical">
        {t('generate_predictive_report_dialog.confirm.no_reports_left')}
      </Alert>

      {canPurchase ? (
        <ButtonLink full to="/billing/addons">
          {t('generate_predictive_report_dialog.no_reports.cta')}
        </ButtonLink>
      ) : (
        <Text>{t('generate_predictive_report_dialog.no_reports.contact_owner')}</Text>
      )}
    </Stack>
  )
}

const ConfirmDownload: FC<any> = ({ availableReports, onSubmit, status }) => {
  const { t } = useTranslation()
  return (
    <form onSubmit={onSubmit}>
      <Stack space="medium">
        <Alert full tone="caution">
          {t('generate_predictive_report_dialog.confirm.reports_left', {
            amount: availableReports,
          })}
        </Alert>

        <Button full type="submit" loading={status === 'loading'}>
          {t('generate_predictive_report_dialog.confirm.cta')}
        </Button>

        {status === 'error' && (
          <Alert full tone="critical">
            {t('common.messages.error')}
          </Alert>
        )}
      </Stack>
    </form>
  )
}
